import { Switch } from 'antd'
import React from 'react'
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form'

import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import type {
  FormInputControlType,
  FormInputRegisterType,
  OptionType,
  SelectFormControlType,
} from '@/types'

import type { EditableUserField } from '../types/users.types'
import {
  LOGIN_PREFIX_LENGTH,
  sanitizeLogin,
  type UserEditFormType,
} from '../Users.config'
import cn from './UserEditField.module.scss'

const handleLoginKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const target = e.currentTarget
  const start = target.selectionStart || 0
  const end = target.selectionEnd || 0
  const isRange = start !== end

  if (
    (e.key === 'Backspace' || e.key === 'Delete') &&
    !isRange &&
    start <= LOGIN_PREFIX_LENGTH
  ) {
    e.preventDefault()
    return
  }

  if (
    (e.key === 'ArrowLeft' || e.key === 'Home' || e.key === 'ArrowUp') &&
    !e.shiftKey &&
    start <= LOGIN_PREFIX_LENGTH
  ) {
    e.preventDefault()
    target.setSelectionRange(LOGIN_PREFIX_LENGTH, LOGIN_PREFIX_LENGTH)
  }
}

const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  e.target.value = sanitizeLogin(e.target.value)
}

const handleLoginMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
  const target = e.currentTarget
  if (target.selectionStart !== target.selectionEnd) return
  if ((target.selectionStart || 0) < LOGIN_PREFIX_LENGTH) {
    target.setSelectionRange(LOGIN_PREFIX_LENGTH, LOGIN_PREFIX_LENGTH)
  }
}

export type UserEditFormContext = {
  control: Control<UserEditFormType>
  register: UseFormRegister<UserEditFormType>
  errors: FieldErrors<UserEditFormType>
  roleOptions: OptionType[]
  rolesPopupClassName: string
  isOrganisationSelected: boolean
}

type UserEditFieldProps = UserEditFormContext & {
  field: EditableUserField
}

function UserEditField(props: UserEditFieldProps) {
  return (
    <div className={cn.editField}>
      <UserEditControl {...props} />
    </div>
  )
}

function UserEditControl({
  field,
  control,
  register,
  errors,
  roleOptions,
  rolesPopupClassName,
  isOrganisationSelected,
}: UserEditFieldProps) {
  switch (field) {
    case 'enabled':
      return (
        <Controller
          name="enabled"
          control={control}
          render={({ field: enabledField }) => (
            <Switch
              checked={enabledField.value}
              onChange={enabledField.onChange}
              aria-label="Доступ"
            />
          )}
        />
      )

    case 'roles':
      return (
        <HoverElement>
          <AtomSelect
            isOrganisationSelected={isOrganisationSelected}
            formName="roles"
            formControl={control as SelectFormControlType}
            formErrors={errors}
            options={roleOptions}
            mode="multiple"
            maxTagCount="responsive"
            openByHover={false}
            popupClassName={rolesPopupClassName}
            fullWidth
          />
        </HoverElement>
      )

    case 'login':
      return (
        <AtomFormInput
          isOrganisationSelected={isOrganisationSelected}
          location="userEdit"
          name="login"
          control={control as FormInputControlType}
          register={register as FormInputRegisterType}
          errors={errors}
          setValueAs={sanitizeLogin}
          handleChangeInput={handleLoginChange}
          handleInputKeyDown={handleLoginKeyDown}
          onMouseUp={handleLoginMouseUp}
          inputMode="numeric"
          fullWidth
        />
      )

    default:
      return (
        <AtomFormInput
          isOrganisationSelected={isOrganisationSelected}
          location="userEdit"
          name={field}
          control={control as FormInputControlType}
          register={register as FormInputRegisterType}
          errors={errors}
          fullWidth
        />
      )
  }
}

export default UserEditField
